import { useEffect, useState } from 'react'
import { TextField, MenuItem } from '@mui/material'
import { useTranslation } from 'react-i18next'

import $api from 'http/index'

import { PageHeader, Card, Spinner, EmptyState, ErrorState, UnvButton, Icon, Badge } from 'components'
import styles from './SyllabusBuilderPage.module.scss'

const SyllabusDiffPage = () => {
  const { t } = useTranslation('common')
  const [yearA, setYearA] = useState('')
  const [yearB, setYearB] = useState('')
  const [years, setYears] = useState([])
  const [diff, setDiff] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)

  useEffect(() => {
    const loadYears = async () => {
      try {
        const res = await $api.get('/syllabus/years/')
        const list = res.data?.results || res.data || []
        setYears(list)
      } catch {
        const cur = new Date().getFullYear()
        const arr = []
        for (let y = cur; y >= cur - 5; y -= 1) arr.push({ year: y })
        setYears(arr)
      }
    }
    loadYears()
  }, [])

  const compare = async () => {
    if (!yearA || !yearB) return
    setLoading(true)
    setError(false)
    try {
      const res = await $api.get('/syllabus/diff/', { params: { a: yearA, b: yearB } })
      setDiff(res.data)
    } catch {
      setError(true)
      setDiff(null)
    } finally {
      setLoading(false)
    }
  }

  const renderItem = (item) => {
    const cls =
      item.change === 'added'
        ? styles.added
        : item.change === 'removed'
        ? styles.removed
        : item.change === 'changed'
        ? styles.changed
        : ''
    return (
      <div className={`${styles.diffItem} ${cls}`}>
        <div>
          <strong>{item.subject || item.name}</strong>
        </div>
        <div style={{ fontSize: 12, color: '#555' }}>
          {t('syllabus.credits_short', { value: item.credits ?? '—' })}
          {item.previous_credits != null && item.previous_credits !== item.credits && (
            <span style={{ marginLeft: 8, color: '#B22222' }}>
              {t('syllabus.previous_credits', { value: item.previous_credits })}
            </span>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className={styles.diffPage}>
      <PageHeader
        icon={<Icon name="syllabus" size={28} />}
        title={t('syllabus.diff_title')}
        subtitle={t('auto.syllabusdiff.subtitle', 'Сравнение учебных планов между годами')}
      />

      <Card padding="md" className={styles.diffControls}>
        <Card.Body>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              select
              size="small"
              label={t('syllabus.year_a')}
              value={yearA}
              onChange={(e) => setYearA(e.target.value)}
              style={{ minWidth: 160 }}
            >
              <MenuItem value="">{t('syllabus.not_selected')}</MenuItem>
              {years.map((y) => (
                <MenuItem key={y.year || y.id} value={y.year || y.id}>
                  {y.year || y.label || y.id}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label={t('syllabus.year_b')}
              value={yearB}
              onChange={(e) => setYearB(e.target.value)}
              style={{ minWidth: 160 }}
            >
              <MenuItem value="">{t('syllabus.not_selected')}</MenuItem>
              {years.map((y) => (
                <MenuItem key={y.year || y.id} value={y.year || y.id}>
                  {y.year || y.label || y.id}
                </MenuItem>
              ))}
            </TextField>
            <UnvButton
              variant="primary"
              iconLeft="refresh"
              onClick={compare}
              disabled={!yearA || !yearB}
              loading={loading}
            >
              {t('syllabus.compare')}
            </UnvButton>
          </div>
        </Card.Body>
      </Card>

      {loading ? (
        <Spinner.Center />
      ) : error ? (
        <ErrorState onRetry={compare} description={t('schedule.section_in_dev')} />
      ) : !diff ? (
        <EmptyState iconName="syllabus" title={t('syllabus.select_two_years')} />
      ) : (
        <div className={styles.diffGrid}>
          <Card padding="md" className={styles.diffColumn}>
            <Card.Header title={t('syllabus.year_a_label', { year: yearA })} />
            <Card.Body>
              {(diff.left || diff.a || []).map((it, i) => (
                <div key={i}>{renderItem(it)}</div>
              ))}
              {(diff.left || diff.a || []).length === 0 && (
                <EmptyState iconName="folder" title={t('syllabus.no_data')} variant="compact" />
              )}
            </Card.Body>
          </Card>
          <Card padding="md" className={styles.diffColumn}>
            <Card.Header title={t('syllabus.year_b_label', { year: yearB })} />
            <Card.Body>
              {(diff.right || diff.b || []).map((it, i) => (
                <div key={i}>{renderItem(it)}</div>
              ))}
              {(diff.right || diff.b || []).length === 0 && (
                <EmptyState iconName="folder" title={t('syllabus.no_data')} variant="compact" />
              )}
            </Card.Body>
          </Card>
        </div>
      )}
    </div>
  )
}

export default SyllabusDiffPage
